/* eslint max-lines: off */

import * as yup from "yup";
import cloneDeep from "lodash/cloneDeep";
import i18next from "i18next";
import { PUBLIC_VIEWER_ROUTE, VIEWER_ROUTE, getBackendBaseUrl } from "../constants";
import { SubRestrictionRecord } from "../actions/publicViewer/types";
import { SegmentObject } from "../actions/regulation/interface";
import { CurrentUserInterface } from "../actions/User/types";
import { SelectValue } from "../interfaces/Select/Value";

/* eslint-disable prefer-named-capture-group */

/** Reads a File and resolves with its base64-encoded data URL string. */
export const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (): void => {
            if (typeof reader.result === "string") {
                resolve(reader.result);

                return;
            }

            reject(new Error("Error reading file."));
        };
        reader.onerror = (): void => reject(new Error("Error reading file."));
        reader.readAsDataURL(file);
    });

export function getSubActivityKey(str1: string, str2: string): string {
    return `${str1}-${str2}`;
}

export function truncateText(text: string | null, maxLength = 50): string {
    if (!text) return "";
    if (text.length <= maxLength) return text;

    return `${text.substring(0, maxLength)}...`;
}

export function getAssetKey(str1: string, str2: string): string {
    return `${str1}-Asset-${str2}`;
}

export function isDictionaryEmpty(dict: object): boolean {
    return Object.getOwnPropertyNames(dict).length === 0;
}

export function validateYupSchema(
    type: string,
    name: string,
    required = true,
    params: {
        maxNumber: number;
        minNumber: number;
    } = {
        maxNumber: Number.MAX_SAFE_INTEGER,
        minNumber: Number.MIN_SAFE_INTEGER
    }
    /* eslint-disable-next-line */
): any {
    /* eslint-disable-next-line */
    function func(data: any): typeof yup {
        return required ? data.required(`${name} ${i18next.t("is required")}`) : data;
    }

    /* eslint-disable complexity */
    function validate(): typeof yup {
        const { minNumber } = params;

        switch (type) {
            case "number":
                return func(
                    yup
                        .number()
                        .min(
                            minNumber,
                            `${name} ${i18next.t("must be greater than or equal to")} ${minNumber}`
                        )
                        .max(
                            params.maxNumber,
                            `${name} ${i18next.t("must be less than or equal to")} ${minNumber}`
                        )
                );
            case "text":
            case "textArea":
            case "password":
                return func(yup.string());
            case "email":
                return func(yup.string().email());
            case "date":
            case "time":
            case "dateTimePicker":
            case "datePicker":
            case "timePicker":
                return func(yup.date().nullable());
            case "select":
                return func(
                    yup
                        .object<SelectValue>()
                        .nullable()
                        .test(
                            "select",
                            `${i18next.t(name)} ${i18next.t("is required")}`,
                            (selectedObject: SelectValue): selectedObject is SelectValue => {
                                if (
                                    !isDictionaryEmpty(selectedObject) &&
                                    selectedObject.label &&
                                    selectedObject.value
                                ) {
                                    return true;
                                }

                                return false;
                            }
                        )
                );
            case "phone":
                return func(
                    yup
                        .string()
                        .matches(
                            /^\+?([0-9]{2})\)?[-. ]?([0-9]{10})$/u,
                            `${i18next.t("Should be phone number")} (+91...)`
                        )
                        .nullable()
                );
            case "startDate":
                return func(yup.date().nullable());
            case "checkbox":
                return func(yup.bool());
            case "array":
                return func(yup.array());
            case "file":
                return func(yup.mixed());
            default:
                break;
        }

        // If no case satisfies
        return func(yup.string());
    }
    /* eslint-enable complexity */

    return validate();
}

export function capitalizeString(string: string): string {
    const len = string.length;

    if (len === 0) {
        return "";
    }

    if (len === 1) {
        return string.toUpperCase();
    }

    return string[0].toUpperCase() + string.substr(1);
}

export function isPathPublicViewer(): boolean {
    return (
        window.location.pathname === `${PUBLIC_VIEWER_ROUTE}` ||
        window.location.pathname === `${PUBLIC_VIEWER_ROUTE.slice(0, -1)}` ||
        window.location.pathname === "/"
    );
}

export function isPathViewer(): boolean {
    const path = window.location.pathname;

    return path.includes(VIEWER_ROUTE);
}

export interface PublicViewerRecord {
    activity: string;
    color: string;
    dashStyle: string;
    displayName: string;
    filterSectionName: string;
    subActivity: string;
    userclasscategory: number;
}

export interface GlobalFiltersData {
    // eslint-disable-next-line camelcase
    sub_activity: string;
    date: Date;
    time: Date;
}

export function mapPublicViewerData(
    subActivity: Set<string>,
    publicViewerData: SubRestrictionRecord[]
): Array<PublicViewerRecord> {
    const displayName: Array<PublicViewerRecord> = [];

    publicViewerData.forEach((ele) => {
        if (subActivity.has(getSubActivityKey(ele.display_name, ele.filter_section))) {
            displayName.push({
                activity: ele.activity,
                color: ele.color,
                dashStyle: ele.dash_style,
                displayName: ele.display_name,
                filterSectionName: ele.filter_section,
                subActivity: ele.reason,
                userclasscategory: ele.userclasscategory
            });
        }
    });

    return displayName;
}

export function cloneDeckGlMapObject(eObject: SegmentObject): object {
    return { object: cloneDeep(eObject.object) };
}

export function getRandomString(): string {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;

    for (let i = 0; i < 5; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
}

export function getURLParam(param: string): string | null {
    return new URLSearchParams(window.location.search).get(param);
}

export function isCurrentUserPresentInGroups(
    currentUser: CurrentUserInterface,
    groupName: string | string[]
): boolean {
    const { groups } = currentUser;

    if (!groups.length) {
        return false;
    }

    let set = new Set();

    if (typeof groupName === "string") {
        set.add(groupName);
    } else {
        set = new Set(groupName);
    }

    return groups.some((group) => set.has(group.name));
}

export function getAssetIconUrl(url: string, isLocalIcon = false): string {
    if (process.env.REACT_APP_ENVIRONMENT?.toLowerCase() !== "local" && url?.includes("static")) {
        return `https://${window.location.hostname}${url}`;
    }
    if (!isLocalIcon && process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "local") {
        return (
            process.env.REACT_APP_BASE_URL?.substring(
                0,
                process.env.REACT_APP_BASE_URL.length - 1
            ) + url
        );
    }

    return getBackendBaseUrl() + url;
}
